import { useId, useMemo, useState, type ReactNode } from 'react';
import type { AuditLog } from '@kawai/shared';
import { Button, Input, Select, ZebraTable } from '@kawai/ui';
import { useAuth } from '../lib/auth.js';
import { useAuditLogs } from '../lib/hooks/useAuditLogs.js';
import {
  AUDIT_LOGS_PAGE_SIZES,
  DEFAULT_AUDIT_LOGS_PAGE_SIZE,
  type AuditLogsFilters,
  type AuditLogsPageSize,
} from '../lib/repositories/auditLogs.js';
import { AuditLogDiff } from '../components/AuditLogDiff.js';
import { datetimeLocalToIsoJst, isoToDatetimeLocalJst } from '../lib/datetimeJst.js';
import { CARD, CARD_TITLE, MONO } from '../lib/uiClasses.js';
import { PageHeader } from '../components/PageHeader.js';
import { Notice } from '../components/Notice.js';

/**
 * S-08: audit_logs 横断ビュー (saas-admin only)。
 *
 * - filter: tenantId / action / actorUid / 期間 (startAt / endAt)
 * - 一覧: createdAt / actorUid / action / targetType / targetId / before・after preview
 * - pagination: Firestore cursor based (25 / 50 / 100 件選択可)
 * - 行クリックで before/after JSON diff 展開 (1 行のみ展開)
 *
 * 設計:
 * - cursor ベース pagination (count() は使わない / コスト面)
 * - filter / pageSize 変更時は cursor stack を reset (常に 1 ページ目から)
 * S-137: 見た目は docs/UI-GUIDE.md の v2（白カード 30/26・ZebraTable・入力は @kawai/ui）。
 */
export function AuditLogsPage() {
  const { email, signOut } = useAuth();

  const [pendingFilters, setPendingFilters] = useState<AuditLogsFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<AuditLogsFilters>({});
  const [pageSize, setPageSize] = useState<AuditLogsPageSize>(
    DEFAULT_AUDIT_LOGS_PAGE_SIZE,
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const tenantIdId = useId();
  const actionId = useId();
  const actorUidId = useId();
  const startAtId = useId();
  const endAtId = useId();
  const pageSizeId = useId();

  const { query, pageIndex, canGoNext, canGoPrev, goNext, goPrev, reset } = useAuditLogs({
    filters: appliedFilters,
    pageSize,
  });

  // S-08b a: filter / pageSize 変更時の cursor stack 初期化は handler 内で同期呼び出しする。
  // useEffect 経由だと「古い cursorStack で fetch1 → effect で reset → 新しい queryKey で fetch2」
  // の double-fetch が発生するため、ハンドラ内で reset() を先に呼んで React 18 の batch で 1 回に集約する。
  function handleApply() {
    reset();
    setAppliedFilters(pendingFilters);
    setExpandedId(null);
  }

  function handleClear() {
    reset();
    setPendingFilters({});
    setAppliedFilters({});
    setExpandedId(null);
  }

  function handlePageSizeChange(newSize: AuditLogsPageSize) {
    reset();
    setPageSize(newSize);
  }

  const items = query.data?.items ?? [];

  return (
    <main className="mx-auto max-w-[1280px] p-6">
      <PageHeader
        title="監査ログ (audit_logs)"
        description="SaaS 管理者ビュー（テナント横断 / S-08）"
        email={email}
        links={[{ to: '/', label: '← テナント一覧' }]}
        onSignOut={() => {
          void signOut();
        }}
      />

      <section className={`mt-6 ${CARD}`} aria-label="フィルタ">
        <h2 className={CARD_TITLE}>フィルタ</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <FilterField label="tenantId" htmlFor={tenantIdId}>
            <Input
              id={tenantIdId}
              type="text"
              value={pendingFilters.tenantId ?? ''}
              onChange={(e) =>
                setPendingFilters((p) => ({ ...p, tenantId: e.target.value }))
              }
              placeholder="kawai-takefu21ss"
            />
          </FilterField>

          <FilterField label="action" htmlFor={actionId}>
            <Input
              id={actionId}
              type="text"
              value={pendingFilters.action ?? ''}
              onChange={(e) =>
                setPendingFilters((p) => ({ ...p, action: e.target.value }))
              }
              placeholder="booking.cancel"
            />
          </FilterField>

          <FilterField label="actorUid" htmlFor={actorUidId}>
            <Input
              id={actorUidId}
              type="text"
              value={pendingFilters.actorUid ?? ''}
              onChange={(e) =>
                setPendingFilters((p) => ({ ...p, actorUid: e.target.value }))
              }
              placeholder="line:U1 / saas-admin uid"
            />
          </FilterField>

          <FilterField label="開始 (JST)" htmlFor={startAtId}>
            <Input
              id={startAtId}
              type="datetime-local"
              value={isoToDatetimeLocalJst(pendingFilters.startAt ?? '')}
              onChange={(e) =>
                setPendingFilters((p) => ({
                  ...p,
                  startAt: datetimeLocalToIsoJst(e.target.value),
                }))
              }
            />
          </FilterField>

          <FilterField label="終了 (JST)" htmlFor={endAtId}>
            <Input
              id={endAtId}
              type="datetime-local"
              value={isoToDatetimeLocalJst(pendingFilters.endAt ?? '')}
              onChange={(e) =>
                setPendingFilters((p) => ({
                  ...p,
                  endAt: datetimeLocalToIsoJst(e.target.value),
                }))
              }
            />
          </FilterField>

          <FilterField label="ページサイズ" htmlFor={pageSizeId}>
            <Select
              id={pageSizeId}
              value={pageSize}
              onChange={(e) =>
                handlePageSizeChange(Number(e.target.value) as AuditLogsPageSize)
              }
            >
              {AUDIT_LOGS_PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s} 件 / page
                </option>
              ))}
            </Select>
          </FilterField>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button type="button" onClick={handleApply}>
            フィルタ適用
          </Button>
          <Button type="button" variant="secondary" onClick={handleClear}>
            条件クリア
          </Button>
        </div>
      </section>

      <section className={`mt-6 ${CARD}`} aria-label="監査ログ一覧">
        {query.isLoading && (
          <p role="status" aria-live="polite" className="text-body text-ink-3">
            読み込み中…
          </p>
        )}

        {query.error && (
          <Notice role="alert">
            audit_logs の取得に失敗しました: {query.error.message}
          </Notice>
        )}

        {!query.isLoading && !query.error && (
          <>
            <div className="overflow-x-auto">
              <ZebraTable aria-label="監査ログ">
                <thead>
                  <ZebraTable.HeadRow>
                    <ZebraTable.Th edge="first" className="whitespace-nowrap">
                      createdAt
                    </ZebraTable.Th>
                    <ZebraTable.Th className="whitespace-nowrap">tenantId</ZebraTable.Th>
                    <ZebraTable.Th className="whitespace-nowrap">action</ZebraTable.Th>
                    <ZebraTable.Th className="whitespace-nowrap">
                      targetType / targetId
                    </ZebraTable.Th>
                    <ZebraTable.Th className="whitespace-nowrap">actorUid</ZebraTable.Th>
                    <ZebraTable.Th edge="last" className="whitespace-nowrap">
                      before/after preview
                    </ZebraTable.Th>
                  </ZebraTable.HeadRow>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <ZebraTable.Row>
                      <ZebraTable.Td colSpan={6} align="center" tone="muted" padding="lg">
                        該当する監査ログがありません
                      </ZebraTable.Td>
                    </ZebraTable.Row>
                  ) : (
                    items.map((log) => (
                      <AuditLogRow
                        key={log.id}
                        log={log}
                        expanded={expandedId === log.id}
                        onToggle={() =>
                          setExpandedId((cur) => (cur === log.id ? null : log.id))
                        }
                      />
                    ))
                  )}
                </tbody>
              </ZebraTable>
            </div>

            <nav
              className="mt-4 flex flex-wrap items-center gap-2"
              aria-label="ページネーション"
            >
              <span className="mr-2 text-label text-ink-3">page {pageIndex}</span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={goPrev}
                disabled={!canGoPrev}
              >
                ← 前のページ
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={goNext}
                disabled={!canGoNext}
              >
                次のページ →
              </Button>
              {query.isFetching && (
                <span className="ml-2 text-label text-ink-3" aria-live="polite">
                  取得中…
                </span>
              )}
            </nav>
          </>
        )}
      </section>
    </main>
  );
}

// ---------- row + diff ----------

interface AuditLogRowProps {
  log: AuditLog;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * 展開行はゼブラの偶奇に混ざらないよう `ZebraTable.Row` を使わず素の tr（親行と同じ面）。
 * diff 本体（AuditLogDiff）が自分の面（bg-surface-2）を持つ。
 */
function AuditLogRow({ log, expanded, onToggle }: AuditLogRowProps) {
  const previewText = useMemo(
    () => buildPreview(log.before, log.after),
    [log.before, log.after],
  );

  return (
    <>
      <ZebraTable.Row className="align-top">
        <ZebraTable.Td className={`${MONO} whitespace-nowrap`}>
          {log.createdAt}
        </ZebraTable.Td>
        <ZebraTable.Td className={MONO}>{log.tenantId ?? '(none)'}</ZebraTable.Td>
        <ZebraTable.Td>
          <code
            className={`${MONO} rounded-lg bg-brand-tint px-2 py-0.5 font-medium text-brand-ink`}
          >
            {log.action}
          </code>
        </ZebraTable.Td>
        <ZebraTable.Td className={MONO}>
          <div>
            {log.targetType}
            <br />
            <span className="text-ink-3">{log.targetId}</span>
          </div>
        </ZebraTable.Td>
        <ZebraTable.Td className={MONO}>{log.actorUid}</ZebraTable.Td>
        <ZebraTable.Td>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={expanded ? '閉じる' : 'before/after を展開'}
            className="flex w-full items-center gap-2 text-left text-label text-ink hover:text-brand-ink"
          >
            <span
              className={`${MONO} flex-1 overflow-hidden text-ellipsis whitespace-nowrap`}
            >
              {previewText}
            </span>
            <span aria-hidden className="text-ink-3">
              {expanded ? '▴' : '▾'}
            </span>
          </button>
        </ZebraTable.Td>
      </ZebraTable.Row>
      {expanded && (
        <tr>
          <td colSpan={6} className="px-3.5 pt-1 pb-4">
            <AuditLogDiff before={log.before} after={log.after} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * S-08b e: 一覧行用 preview。before/after を比較して「変更された key 名」だけを表示し、
 * 80 文字 fallback truncate は最後の手段としてのみ適用する。
 *
 * - before / after どちらも null → '(empty)'
 * - before のみ null → 'created: <keys>'
 * - after のみ null → 'deleted: <keys>'
 * - 両方 object → top-level の差分キーを 'changed: <keys>' で表示
 * - primitive 比較や深い nested は意図的にカバーしない (audit_logs 用途では十分)
 */
export function buildPreview(before: unknown, after: unknown): string {
  const b = before === null || before === undefined ? null : before;
  const a = after === null || after === undefined ? null : after;
  if (b === null && a === null) return '(empty)';
  if (b === null) return formatPreview('created', topLevelKeys(a));
  if (a === null) return formatPreview('deleted', topLevelKeys(b));
  const changed = diffTopLevelKeys(b, a);
  if (changed.length === 0) return '(no diff)';
  return formatPreview('changed', changed);
}

function topLevelKeys(value: unknown): string[] {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(value as Record<string, unknown>);
  }
  return [];
}

function diffTopLevelKeys(before: unknown, after: unknown): string[] {
  // object 以外 (primitive / array) は粒度ある比較ができないので "value" を返す
  if (
    before === null ||
    after === null ||
    typeof before !== 'object' ||
    typeof after !== 'object' ||
    Array.isArray(before) ||
    Array.isArray(after)
  ) {
    return safeStringify(before) === safeStringify(after) ? [] : ['value'];
  }
  const allKeys = new Set<string>([
    ...Object.keys(before as Record<string, unknown>),
    ...Object.keys(after as Record<string, unknown>),
  ]);
  const changed: string[] = [];
  for (const k of allKeys) {
    const bv = (before as Record<string, unknown>)[k];
    const av = (after as Record<string, unknown>)[k];
    if (safeStringify(bv) !== safeStringify(av)) changed.push(k);
  }
  return changed;
}

function formatPreview(
  prefix: 'created' | 'deleted' | 'changed',
  keys: string[],
): string {
  if (keys.length === 0) return prefix;
  const text = `${prefix}: ${keys.join(', ')}`;
  return text.length > 80 ? text.slice(0, 80) + '…' : text;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// ---------- small layout ----------

interface FilterFieldProps {
  label: string;
  htmlFor: string;
  children: ReactNode;
}

function FilterField({ label, htmlFor, children }: FilterFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-label font-medium text-ink-2">
        {label}
      </label>
      {children}
    </div>
  );
}
